import React, { useMemo, useState } from 'react';
import { Badge, type BadgeProps } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import CustomCombobox from '@/shared/ui/custom/combobox';
import { AddNewTradeDialog } from '@/shared/forms';
import {
  Building2,
  Check,
  Loader2,
  Mail,
  Pencil,
  Phone,
  Plus,
  RotateCw,
  Search,
  Trash2,
} from 'lucide-react';
import {
  getProjectTeamErrorMessage,
  type TradePartnersResponse,
} from '../api/api';
import { useTradePartners } from '../hooks/useProjectTeam';
import type {
  Client,
  TradePartner,
  TradePartnerApplication,
  TradePartnerInput,
} from '../types/types';

interface TradePartnersTabProps {
  projectId: number;
  clientId: number;
  canManage: boolean;
  initialData?: TradePartnersResponse;
  onError: (message: string | null) => void;
  onSuccess: (message: string | null) => void;
}

const emptyForm = (defaultProjectRoleId: number | null = null): TradePartnerInput => ({
  company_name: '',
  partner_client_id: null,
  trade_id: null,
  default_project_role_id: defaultProjectRoleId,
  primary_contact_name: '',
  primary_contact_email: '',
  primary_contact_phone: '',
  is_active: true,
});

interface CompanyNameInputProps {
  clients: Client[];
  value: string;
  selectedClientId?: number | null;
  onChange: (companyName: string, clientId: number | null) => void;
}

const CompanyNameInput: React.FC<CompanyNameInputProps> = ({
  clients,
  value,
  selectedClientId,
  onChange,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const query = value.trim().toLowerCase();
  const filteredClients = useMemo(
    () =>
      clients
        .filter((client) => client.name.toLowerCase().includes(query))
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, 50),
    [clients, query],
  );

  return (
    <div className="relative">
      <Input
        id="partner-company"
        value={value}
        onFocus={() => setIsFocused(true)}
        onBlur={() => window.setTimeout(() => setIsFocused(false), 120)}
        onChange={(event) => {
          const nextValue = event.target.value;
          const exactMatch = clients.find(
            (client) => client.name.toLowerCase() === nextValue.trim().toLowerCase(),
          );
          onChange(nextValue, exactMatch?.id ?? null);
        }}
        placeholder="Start typing a company name"
        autoComplete="off"
        required
        className={selectedClientId ? 'pr-9' : undefined}
        aria-autocomplete="list"
        aria-expanded={isFocused && query.length > 0}
      />
      {selectedClientId && (
        <Check className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary" />
      )}
      {isFocused && query.length > 0 && (
        <div
          role="listbox"
          className="absolute z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-slate"
        >
          {filteredClients.length > 0 ? (
            filteredClients.map((client) => (
              <button
                key={client.id}
                type="button"
                role="option"
                aria-selected={client.id === selectedClientId}
                onMouseDown={(event) => {
                  event.preventDefault();
                  onChange(client.name, client.id);
                  setIsFocused(false);
                }}
                className="flex w-full items-center rounded-sm px-2 py-2 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent"
              >
                <Check
                  className={`mr-2 h-4 w-4 shrink-0 ${
                    client.id === selectedClientId ? 'text-primary opacity-100' : 'opacity-0'
                  }`}
                />
                <span className="truncate">{client.name}</span>
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              No matching companies.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const effectiveStatus = (
  application?: TradePartnerApplication | null,
): TradePartnerApplication['status'] | 'NONE' => {
  if (!application) return 'NONE';
  if (
    application.status === 'EXPIRED' ||
    (application.status === 'INVITED' &&
      (application.is_expired || application.is_token_expired))
  ) {
    return 'EXPIRED';
  }
  return application.status;
};

const statusPresentation: Record<
  ReturnType<typeof effectiveStatus>,
  { label: string; variant: BadgeProps['variant'] }
> = {
  NONE: { label: 'Not invited', variant: 'secondary' },
  INVITED: { label: 'Invited', variant: 'default' },
  SUBMITTED: { label: 'Onboarded', variant: 'success' },
  REJECTED: { label: 'Rejected', variant: 'destructive' },
  EXPIRED: { label: 'Expired', variant: 'outline' },
};

export const TradePartnersTab: React.FC<TradePartnersTabProps> = ({
  projectId,
  clientId,
  canManage,
  initialData,
  onError,
  onSuccess,
}) => {
  const {
    partners,
    trades,
    projectRoles,
    availableClients,
    isLoading,
    createPartner,
    updatePartner,
    deletePartner,
    createTrade,
    resendApplication,
  } = useTradePartners(projectId, clientId, initialData);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<TradePartner | null>(null);
  const [actionPartner, setActionPartner] = useState<TradePartner | null>(null);
  const [actionDialog, setActionDialog] = useState<'resend' | 'delete' | null>(null);
  const [form, setForm] = useState<TradePartnerInput>(emptyForm);
  const [searchQuery, setSearchQuery] = useState('');

  const projectDefaultRole = useMemo(
    () => projectRoles.find((role) => role.is_default) ?? projectRoles[0] ?? null,
    [projectRoles],
  );
  const getPartnerRole = (partner: TradePartner) =>
    partner.default_project_role ?? projectDefaultRole;
  const filteredPartners = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return partners.filter((partner) => {
      if (!query) return true;
      return [
        partner.company_name,
        partner.partner_client?.name ?? '',
        partner.trade?.name ?? '',
        getPartnerRole(partner)?.name ?? '',
        partner.primary_contact_name,
        partner.primary_contact_email,
        partner.primary_contact_phone,
      ].some((value) => value.toLowerCase().includes(query));
    });
  }, [partners, projectDefaultRole, searchQuery]);

  const closeActionDialog = () => {
    setActionDialog(null);
    setActionPartner(null);
  };

  const openActionDialog = (
    action: 'resend' | 'delete',
    partner: TradePartner,
  ) => {
    setActionPartner(partner);
    setActionDialog(action);
  };

  const openCreateDialog = () => {
    setEditingPartner(null);
    setForm(emptyForm(projectDefaultRole?.id ?? null));
    setIsEditorOpen(true);
  };

  const openEditDialog = (partner: TradePartner) => {
    setEditingPartner(partner);
    setForm({
      company_name: partner.company_name,
      partner_client_id: partner.partner_client?.id ?? null,
      trade_id: partner.trade?.id ?? null,
      default_project_role_id:
        partner.default_project_role?.id ?? projectDefaultRole?.id ?? null,
      primary_contact_name: partner.primary_contact_name,
      primary_contact_email: partner.primary_contact_email,
      primary_contact_phone: partner.primary_contact_phone,
      is_active: partner.is_active,
    });
    setIsEditorOpen(true);
  };

  const handleCreateTrade = async (name: string) => {
    onError(null);
    try {
      const trade = await createTrade.mutateAsync(name);
      setForm((current) => ({ ...current, trade_id: trade.id }));
      onSuccess('Trade added');
    } catch (error) {
      onError(getProjectTeamErrorMessage(error, 'Failed to add trade'));
    }
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    const payload: TradePartnerInput = {
      ...form,
      company_name: form.company_name.trim(),
      primary_contact_name: form.primary_contact_name?.trim() ?? '',
      primary_contact_email: form.primary_contact_email?.trim() ?? '',
      primary_contact_phone: form.primary_contact_phone?.trim() ?? '',
      client_id: clientId,
    };
    onError(null);
    try {
      if (editingPartner) {
        await updatePartner.mutateAsync({ partnerId: editingPartner.id, input: payload });
        onSuccess('Trade partner updated');
      } else {
        await createPartner.mutateAsync(payload);
        onSuccess('Trade partner added');
      }
      setIsEditorOpen(false);
      setEditingPartner(null);
      setForm(emptyForm());
    } catch (error) {
      onError(getProjectTeamErrorMessage(error, 'Failed to save trade partner'));
    }
  };

  const handleAction = async () => {
    const application = actionPartner?.latest_application;
    if (!actionPartner || !actionDialog) return;
    onError(null);
    try {
      if (actionDialog === 'delete') {
        await deletePartner.mutateAsync(actionPartner.id);
        onSuccess('Trade partner removed');
      } else if (actionDialog === 'resend' && application) {
        await resendApplication.mutateAsync({
          partnerId: actionPartner.id,
          applicationId: application.id,
        });
        onSuccess('Application invitation resent');
      }
      closeActionDialog();
    } catch (error) {
      onError(getProjectTeamErrorMessage(error, `Failed to ${actionDialog} trade partner`));
    }
  };

  const actionPending =
    deletePartner.isPending ||
    resendApplication.isPending;

  return (
    <div className="rounded-[1.375rem] border border-border bg-card shadow-slate">
      <div className="flex flex-col gap-4 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search partners..."
            aria-label="Search trade partners"
            className="h-10 pl-9"
          />
        </div>
        {canManage && (
          <Button size="sm" onClick={openCreateDialog}>
            <Plus className="mr-2 h-4 w-4" />
            Add Partner
          </Button>
        )}
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Company</TableHead>
            <TableHead>Trade</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Default Role</TableHead>
            <TableHead>Primary Contact</TableHead>
            {canManage && (
              <TableHead className="w-48 text-right">Actions</TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell
                colSpan={canManage ? 6 : 5}
                className="h-28 text-center"
              >
                <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
                <span className="sr-only">Loading trade partners</span>
              </TableCell>
            </TableRow>
          ) : filteredPartners.length > 0 ? (
            filteredPartners.map((partner) => {
              const status = effectiveStatus(partner.latest_application);
              const presentation = statusPresentation[status];
              const canResend =
                canManage && ['INVITED', 'EXPIRED'].includes(status);
              return (
                <TableRow key={partner.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{partner.company_name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {partner.trade ? (
                      <Badge variant="secondary" className="font-normal">
                        {partner.trade.name}
                      </Badge>
                    ) : (
                      <span className="text-sm text-muted-foreground">Unassigned</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={presentation.variant}>{presentation.label}</Badge>
                  </TableCell>
                  <TableCell>
                    {getPartnerRole(partner) ? (
                      <Badge variant="outline" className="font-normal">
                        {getPartnerRole(partner)?.name}
                      </Badge>
                    ) : (
                      <span className="text-sm text-muted-foreground">No role</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1 text-sm">
                      <div>{partner.primary_contact_name || 'No contact'}</div>
                      {partner.primary_contact_email && (
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Mail className="h-3.5 w-3.5" />
                          {partner.primary_contact_email}
                        </div>
                      )}
                      {partner.primary_contact_phone && (
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Phone className="h-3.5 w-3.5" />
                          {partner.primary_contact_phone}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  {canManage && (
                    <TableCell>
                      <div className="flex flex-wrap justify-end gap-1">
                        {canResend && (
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Resend application to ${partner.company_name}`}
                            onClick={() => openActionDialog('resend', partner)}
                          >
                            <RotateCw className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(partner)}
                          aria-label={`Edit ${partner.company_name}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openActionDialog('delete', partner)}
                          aria-label={`Remove ${partner.company_name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell
                colSpan={canManage ? 6 : 5}
                className="h-28 text-center text-muted-foreground"
              >
                No trade partners found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {canManage && (
        <Dialog open={isEditorOpen} onOpenChange={setIsEditorOpen}>
          <DialogContent className="sm:max-w-[640px]">
            <DialogHeader>
              <DialogTitle>
                {editingPartner ? 'Edit Trade Partner' : 'Add Trade Partner'}
              </DialogTitle>
              <DialogDescription>
                Track the partner company, trade scope, and primary project contact.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSave} className="space-y-4">
              <fieldset className="rounded-xl border border-border bg-muted/30 p-4">
                <legend className="px-2 text-sm font-semibold text-foreground">
                  Company &amp; Project Assignment
                </legend>
                <p className="mb-4 text-sm text-muted-foreground">
                  Required project details.
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="partner-company">Company Name *</Label>
                    <CompanyNameInput
                      clients={availableClients}
                      value={form.company_name}
                      selectedClientId={form.partner_client_id}
                      onChange={(companyName, partnerClientId) =>
                        setForm((current) => ({
                          ...current,
                          company_name: companyName,
                          partner_client_id: partnerClientId,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Trade *</Label>
                    <CustomCombobox
                      options={trades.map((trade) => ({ value: trade.id, label: trade.name }))}
                      value={form.trade_id ?? ''}
                      onChange={(tradeId) =>
                        setForm((current) => ({ ...current, trade_id: tradeId || null }))
                      }
                      placeholder="Select trade"
                      searchPlaceholder="Search trades..."
                      addNewLabel="Add New Trade"
                      emptyMessage="No trades found."
                      onAddNew={handleCreateTrade}
                      AddNewDialog={AddNewTradeDialog}
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label>Default Project Role *</Label>
                    <CustomCombobox
                      options={projectRoles.map((role) => ({
                        value: String(role.id),
                        label: role.name,
                      }))}
                      value={
                        form.default_project_role_id
                          ? String(form.default_project_role_id)
                          : ''
                      }
                      onChange={(roleId) =>
                        setForm((current) => ({
                          ...current,
                          default_project_role_id: roleId ? Number(roleId) : null,
                        }))
                      }
                      placeholder="Select the role assigned after onboarding"
                      searchPlaceholder="Search roles..."
                      emptyMessage="No roles found."
                    />
                  </div>
                </div>
              </fieldset>

              <fieldset className="rounded-xl border border-border bg-muted/30 p-4">
                <legend className="px-2 text-sm font-semibold text-foreground">
                  Primary Contact
                </legend>
                <p className="mb-4 text-sm text-muted-foreground">
                  This contact receives the onboarding invitation.
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="partner-contact">Contact Name</Label>
                    <Input
                      id="partner-contact"
                      value={form.primary_contact_name}
                      placeholder="First and last name"
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          primary_contact_name: event.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="partner-email">Contact Email</Label>
                    <Input
                      id="partner-email"
                      type="email"
                      value={form.primary_contact_email}
                      placeholder="name@company.com"
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          primary_contact_email: event.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="partner-phone">Contact Phone</Label>
                    <Input
                      id="partner-phone"
                      type="tel"
                      value={form.primary_contact_phone}
                      placeholder="Optional phone number"
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          primary_contact_phone: event.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              </fieldset>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsEditorOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    createPartner.isPending ||
                    updatePartner.isPending ||
                    !form.company_name.trim() ||
                    !form.trade_id ||
                    !form.default_project_role_id
                  }
                >
                  {(createPartner.isPending || updatePartner.isPending) && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {editingPartner ? 'Save Changes' : 'Add Partner'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      <Dialog
        open={actionDialog === 'resend'}
        onOpenChange={(open) => !open && closeActionDialog()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resend application invitation</DialogTitle>
            <DialogDescription>
              Send a new expiring application link to {actionPartner?.primary_contact_email}.
              The previous link will stop working.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={closeActionDialog}>Cancel</Button>
            <Button onClick={handleAction} disabled={actionPending}>Resend Invitation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={actionDialog === 'delete'}
        onOpenChange={(open) => !open && closeActionDialog()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove {actionPartner?.company_name}</DialogTitle>
            <DialogDescription>
              Remove this trade partner from the project? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={closeActionDialog}>Cancel</Button>
            <Button variant="destructive" onClick={handleAction} disabled={actionPending}>
              Remove Partner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
